'use client'

import { Stack } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'

import BillingDraftCard, { type DraftApplyOutcome } from './BillingDraftCard'
import DraftCompletenessCard from './DraftCompletenessCard'
import TaxGuidanceCard from './TaxGuidanceCard'
import { stashDraftForWizard } from './pendingDraft'
import type { BillingDraft } from './types'
import useSessionScope from './useSessionScope'

type BillingDraftMessageBlockProps = {
  drafts: BillingDraft[]
  /**
   * Seeds the chat input with the localized "apply this draft directly" request (the user still sends
   * it). Omitted on read-only surfaces such as the shared conversation page, which hides the action.
   */
  onAskApply?: (message: string) => void
  /** Called once "Review in form" has started navigating, so the surface can close itself. */
  onNavigate?: () => void
  disabled?: boolean
}

/**
 * The billing proposals of one assistant turn outside the composer panel (global bar, shared
 * conversation page; contract §8): draft cards in the bar variant, tax guidance without an apply
 * action, and the completeness checklist as links only. "Review in form" stashes the draft and opens
 * the wizard, which applies it on mount.
 */
export default function BillingDraftMessageBlock({ drafts, onAskApply, onNavigate, disabled }: BillingDraftMessageBlockProps) {
  const t = useTranslations('features.BillingComposer')
  const router = useRouter()
  const sessionScope = useSessionScope()

  const review = useCallback(
    (draft: BillingDraft, selectedPaths: string[] | undefined): DraftApplyOutcome => {
      const route = stashDraftForWizard(draft, selectedPaths, sessionScope)
      if (!route) return { kind: 'failed' }
      // The click came from the keyboard or mouse inside the bar; drop focus so the wizard does not
      // land with a stale focus ring on whatever element happens to be first.
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
      router.push(route)
      onNavigate?.()
      return { kind: 'navigated' }
    },
    [onNavigate, router, sessionScope]
  )

  const askApply = useCallback(
    (draft: BillingDraft) => {
      const title = draft.title?.trim()
      onAskApply?.(title ? t('draft_askApplyMessageTitled', { title }) : t('draft_askApplyMessage'))
    },
    [onAskApply, t]
  )

  if (drafts.length === 0) return null

  return (
    <Stack spacing={1} sx={{ width: '100%' }} data-cy="billing-draft-block">
      {drafts.map((draft) => (
        <Stack key={draft.draftId} spacing={1}>
          <BillingDraftCard
            variant="bar"
            draft={draft}
            onReview={(selectedPaths) => review(draft, selectedPaths)}
            onAskApply={onAskApply ? () => askApply(draft) : undefined}
            disabled={disabled}
          />
          {draft.taxGuidance && <TaxGuidanceCard guidance={draft.taxGuidance} disabled={disabled} />}
          {draft.completeness && <DraftCompletenessCard completeness={draft.completeness} onNavigate={onNavigate} />}
        </Stack>
      ))}
    </Stack>
  )
}
